import LegalLayout, { Section } from "../LegalLayout";
import { BUSINESS } from "../businessInfo";

const mail = (
  <a className="text-primary-600 hover:text-primary-700" href={`mailto:${BUSINESS.supportEmail}`}>
    {BUSINESS.supportEmail}
  </a>
);

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      intro={
        <p>
          {BUSINESS.brandName} ("we", "our", or "us"), operated by {BUSINESS.legalName}, is committed
          to protecting the privacy of restaurants, their staff, and their diners. This Privacy Policy
          explains how we collect, use, disclose, and safeguard information when you use our
          restaurant management platform — the web dashboard at{" "}
          <a className="text-primary-600 hover:text-primary-700" href={BUSINESS.website}>
            {BUSINESS.website.replace(/^https?:\/\//, "")}
          </a>
          , the POS and Captain apps (Windows, Android, iOS), the Kitchen Display System, and the
          QR / self-order pages your diners use (together, the "Service"). By using the Service you
          consent to the practices described here. If you do not agree, please do not use the Service.
        </p>
      }
    >
      <Section heading="1. Information we collect">
        <p><strong>Account &amp; authentication information.</strong> When a restaurant owner or staff
          member registers, we collect name, email address, phone number, and business details such as
          business name, address, GSTIN and FSSAI. Sign-in uses email and password via Google Firebase
          Authentication. Staff sign in to the POS / Captain apps with a 4-digit PIN, which we store
          only as a one-way hash — never in plain text.</p>
        <p><strong>Business &amp; operational data.</strong> The menu, categories, prices, tables, QR
          codes, orders, KOTs, staff records and permissions, customers, inventory, reservations,
          feedback, and the sales and tax reports you create and store using the Service.</p>
        <p><strong>Diner (guest) order data.</strong> When a diner places an order from a table QR code
          or your online link, we collect the name and mobile number they enter, their table or room
          number, and the items they order. The mobile number is used to link the order, send order
          updates, and — where the restaurant runs a loyalty programme — to award points.</p>
        <p><strong>Subscription &amp; payment information.</strong> Your plan, subscription status, and
          renewal dates. Subscription and any online order payments are processed by our payment
          partner, Razorpay. We receive only a payment reference, amount, and status — we never see or
          store your full card number, UPI PIN, or bank credentials.</p>
        <p><strong>Device &amp; usage data.</strong> Device type, operating system, app version,
          browser, IP address, and information about how you use the Service (features used, pages
          visited) — for delivery, security, and troubleshooting.</p>
      </Section>

      <Section heading="2. How we use your information">
        <p><strong>Provide the Service.</strong> Authenticate you, create and manage your account and
          outlets, take and route orders to the kitchen (KDS) and waiters (Captain), print KOTs and
          bills, manage your menu, tables, staff, inventory and customers, and generate GST-compliant
          bills and reports.</p>
        <p><strong>Process payments &amp; subscriptions.</strong> Create and verify payments through
          Razorpay and activate or renew your plan.</p>
        <p><strong>Send transactional messages.</strong> Order status, "your food is ready", payment
          receipts, and important service notices — by in-app notification, push, email, and, where the
          restaurant enables it, SMS or WhatsApp to the diner's mobile number.</p>
        <p><strong>Support, security &amp; compliance.</strong> Provide customer support, prevent fraud
          and abuse, keep the Service secure and reliable, and meet legal, tax, and accounting
          obligations. We analyse aggregate usage to improve the product. We do <strong>not</strong> sell
          your personal or business data.</p>
      </Section>

      <Section heading="3. Data storage &amp; security">
        <p><strong>Cloud storage.</strong> Business data is stored in our managed database (Supabase /
          PostgreSQL) with encryption in transit and at rest. Every restaurant's rows are isolated from
          every other restaurant by row-level security, so one outlet can never read another's data.</p>
        <p><strong>Authentication &amp; notifications.</strong> Sign-in and push notifications use
          Google Firebase. On the POS and Captain apps, long-lived login tokens are kept in your
          device's encrypted secure storage (OS keystore/keychain), not in plain text.</p>
        <p><strong>Integrity.</strong> Staff and owner PINs are stored as bcrypt hashes; subscription
          state is verified server-side (HMAC signature checks on payments) to prevent tampering.</p>
        <p>We apply access controls, encryption, least-privilege practices, and secure authentication to
          protect your data. No method of electronic storage or transmission is 100% secure, so we
          cannot guarantee absolute security, but we work to protect your information and respond
          promptly to any incident.</p>
      </Section>

      <Section heading="4. Data sharing &amp; third-party processors">
        <p>We do not sell, trade, or rent your personal or business data. We share data only with
          service providers that help us run the Service, under contract, and only as needed:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Supabase</strong> — cloud database and backend hosting.</li>
          <li><strong>Google Firebase</strong> — authentication and push notifications
            (<a className="text-primary-600 hover:text-primary-700" href="https://firebase.google.com/support/privacy">Google's Privacy Policy</a>).</li>
          <li><strong>Razorpay</strong> — payment processing for subscriptions and, where enabled,
            online order payments.</li>
          <li><strong>Delivery aggregators.</strong> If you connect Swiggy, Zomato, or ONDC, order and
            reconciliation data flows between those platforms and your account under their own terms.</li>
        </ul>
        <p><strong>Staff access.</strong> When you add staff to your restaurant, they can view or act on
          your business data based on the role and permissions you grant. You are responsible for
          managing those permissions.</p>
        <p><strong>Legal requirements.</strong> We may disclose data when required by law, to comply with
          legal proceedings, or to protect the rights, property, or safety of our users and the public.</p>
      </Section>

      <Section heading="5. Diner / self-order data">
        <p>The QR / self-order experience is designed to collect the minimum needed to fulfil an order:
          the diner's name, mobile number, table or room, and the items ordered. A diner's active order
          session is kept only in their browser and is discarded when they close the tab. We do not
          build advertising profiles of diners, and we do not sell diner contact details.</p>
      </Section>

      <Section heading="6. Permissions we request">
        <p>The apps request only the permissions needed to run a restaurant:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Internet</strong> — for sync, authentication, live orders, and backups.</li>
          <li><strong>Notifications</strong> — for new-order and "ready" alerts on the POS, KDS, and
            Captain apps.</li>
          <li><strong>Local storage / printing</strong> — to save exported reports and to print KOTs and
            bills to your thermal printer.</li>
          <li><strong>Microphone (Captain app, optional)</strong> — used only for on-device voice
            ordering. Audio is processed on the device for speech-to-text; we do not record or upload it.
            You can use the app without this.</li>
        </ul>
        <p>Diners scan your table QR code with their own phone's camera; our apps do not access a diner's
          camera. We do not request device contacts, location/GPS, call logs, or SMS on the merchant apps.</p>
      </Section>

      <Section heading="7. Communications">
        <p>We send you service and transactional messages (order updates, payment receipts, security and
          account notices). Where a restaurant enables it, order updates may be sent to a diner's mobile
          number by SMS or WhatsApp; that message is sent to the number the diner provided for the order.
          You can opt out of non-essential communications by contacting us.</p>
      </Section>

      <Section heading="8. Data export &amp; portability">
        <p>You can export your menu and business data at any time in CSV and Excel (.xlsx) formats, and
          reports as CSV / Tally XML, directly from the dashboard and POS. We believe in your right to
          take your data with you.</p>
      </Section>

      <Section heading="9. Your rights">
        <p>You have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Access &amp; update</strong> your personal and business information from your
            account settings.</li>
          <li><strong>Export</strong> your data in standard formats (CSV, Excel).</li>
          <li><strong>Delete</strong> — request deletion of your account and associated data by emailing
            us. On a verified request, we delete your data within 30 days, except where retention is
            required by law.</li>
          <li><strong>Withdraw consent</strong> at any time by discontinuing use of the Service and
            requesting deletion.</li>
        </ul>
        <p>To exercise these rights, contact us at {mail}.</p>
      </Section>

      <Section heading="10. Data retention">
        <p>We retain your data while your account is active and as needed to provide the Service. On a
          verified deletion request we remove your personal and business data within 30 days, except
          where we must retain certain records to meet legal obligations (such as tax and accounting
          rules under Indian law).</p>
      </Section>

      <Section heading="11. Cookies &amp; local storage">
        <p>Our website and dashboard use only essential cookies / local storage needed for sign-in
          sessions and core functionality. We do not use third-party advertising or tracking cookies. You
          can control cookies through your browser settings.</p>
      </Section>

      <Section heading="12. Children's privacy">
        <p>The Service is intended for business use and is not directed at individuals under 18. We do not
          knowingly collect personal information from children. If we learn we have, we will delete it
          promptly.</p>
      </Section>

      <Section heading="13. Compliance with Indian law">
        <p>{BUSINESS.brandName} is operated from India and complies with applicable Indian data-protection
          law, including the Digital Personal Data Protection (DPDP) Act, 2023, and the Information
          Technology Act, 2000. We process your personal data on the basis of the consent you give when
          you create an account and use the Service. You may withdraw consent and request deletion as
          described in Section 9.</p>
      </Section>

      <Section heading="14. Information we do not collect">
        <p>For transparency: {BUSINESS.brandName} does <strong>not</strong> collect or access device
          location/GPS, device identifiers (IMEI/Android ID), biometric data, diner contact lists, call
          logs, SMS on merchant devices, browsing history, or health data. We do <strong>not</strong> show
          advertisements or share data with advertising networks.</p>
      </Section>

      <Section heading="15. Changes to this policy">
        <p>We may update this Privacy Policy from time to time. We will post the updated policy here with a
          revised "last updated" date, and for material changes may also notify you by email or in-app
          notice. Continued use of the Service after changes indicates acceptance of the updated policy.</p>
      </Section>

      <Section heading="16. Grievance Officer">
        <p>In accordance with the Information Technology Act, 2000 and the rules made thereunder, the
          Grievance Officer can be reached at {mail} ({BUSINESS.legalName}, India). If you have any
          complaint about the processing of your personal data, we will acknowledge it within 24 hours and
          work to resolve it within 30 days.</p>
      </Section>

      <Section heading="17. Contact us">
        <p>For any questions about this Privacy Policy or our data practices, contact {BUSINESS.legalName}
          {" "}at {mail}
          {BUSINESS.address ? <> or {BUSINESS.address}</> : null}.</p>
      </Section>
    </LegalLayout>
  );
}
